import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { Skeleton } from "../ds";
import { PREVIEW_SKELETON_BG, PREVIEW_STAGE_BG, PreviewThemeToggle, usePreviewTheme, type PreviewTheme } from "./TvFrameTheme";

export interface TvFrameHandle {
  post: (msg: Record<string, unknown>) => void;
}

/** Size of the simulated screen the renderer chose for the current page (adaptive mode). */
export interface TvFrameLayout {
  width: number;
  height: number;
  canvas: { width: number; height: number };
}

export interface TvFramePageInfo {
  id: string;
  name: string;
  section: string | null;
  parentPageId?: string | null;
  showInNavigation?: boolean;
}

/** The canonical TV renderer (/tv-preview/preview.html) embedded at 16:9 --
 * exactly what the office TVs render, used by the workspace, the viewer and
 * the designer so there is one source of truth for how a report looks.
 *
 * Light/dark: follows the user's saved preview preference (default light)
 * unless `theme` is passed. Passed as `theme=` on first load and switched live
 * with `mrp:theme` afterwards, so toggling never reloads or refetches. */
export const TvFrame = forwardRef<
  TvFrameHandle,
  {
    reportId: string;
    source: "admin" | "owner" | "published" | "share";
    /** source="share" only: the share link's token (the frame reads /api/v1/share/{token}). */
    shareToken?: string;
    page?: string | null;
    design?: boolean;
    onLoaded?: (pages: TvFramePageInfo[], pageId: string) => void;
    onPage?: (pageId: string) => void;
    onRendered?: (pageId: string) => void;
    onError?: (message: string) => void;
    className?: string;
    rounded?: boolean;
    reloadKey?: string | number;
    /** Force a mode (the caller owns the toggle). Omit to follow the saved preference. */
    theme?: PreviewTheme;
    /** Show the small floating Light/Dark switch in the corner (default: when `theme` isn't passed). */
    themeToggle?: boolean;
    /** "fixed" = the page's own canvas, uniformly scaled (what the studio shows). Omit for the legacy
     * behaviour (the stage width stretches 0.85-1.35x to fill the frame unless the report says fixed). */
    fit?: "fixed" | "fill";
    /** v3.0: the frame's aspect ratio follows the page's own canvas (A4 portrait/landscape, any size)
     * instead of always being 16:9. Canvases that are TV-shaped stay 16:9. */
    adaptive?: boolean;
    /** Adaptive mode: called whenever the renderer picks a new screen size (page change). */
    onLayout?: (layout: TvFrameLayout) => void;
    /** Version-preview host: do not load the draft/published version; wait for `mrp:loadDefinition`. */
    awaitDefinition?: boolean;
    /** awaitDefinition: the renderer is ready to receive `mrp:loadDefinition`. */
    onReady?: () => void;
    /** The pointer moved over the report (the iframe swallows mouse events, so the host cannot see them): used to reveal a hover toolbar. */
    onPointer?: () => void;
  }
>(function TvFrame({ reportId, source, shareToken, page, design, onLoaded, onPage, onRendered, onError, rounded = true, reloadKey, theme: forced, themeToggle, fit, adaptive, onLayout, awaitDefinition, onReady, onPointer }, ref) {
  const iframe = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const [saved, setSaved] = usePreviewTheme();
  const theme = forced ?? saved;
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const handlers = useRef({ onLoaded, onPage, onRendered, onError, onLayout, onReady, onPointer });
  handlers.current = { onLoaded, onPage, onRendered, onError, onLayout, onReady, onPointer };
  const [layout, setLayout] = useState<TvFrameLayout | null>(null);
  const aspect = adaptive && layout ? layout.width / layout.height : 16 / 9;

  const post = (msg: Record<string, unknown>) => iframe.current?.contentWindow?.postMessage(msg, window.location.origin);
  useImperativeHandle(ref, () => ({ post }));

  useEffect(() => {
    const h = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== iframe.current?.contentWindow) return;
      const m = e.data as { type?: string; pages?: TvFramePageInfo[]; pageId?: string; message?: string; width?: number; height?: number; canvas?: { width: number; height: number } };
      if (m.type === "mrp:loaded" && m.pages) {
        // The URL carried the mode at load time; re-assert in case it changed while loading.
        post({ type: "mrp:theme", theme: themeRef.current });
        handlers.current.onLoaded?.(m.pages, m.pageId ?? "");
      } else if (m.type === "mrp:page" && m.pageId) handlers.current.onPage?.(m.pageId);
      else if (m.type === "mrp:rendered" && m.pageId) {
        setReady(true);
        handlers.current.onRendered?.(m.pageId);
      } else if (m.type === "mrp:error") handlers.current.onError?.(m.message ?? "Failed to load");
      else if (m.type === "mrp:ready") handlers.current.onReady?.();
      else if (m.type === "mrp:pointer") handlers.current.onPointer?.();
      else if (m.type === "mrp:layout" && m.width && m.height) {
        const l = { width: m.width, height: m.height, canvas: m.canvas ?? { width: m.width, height: m.height } };
        setLayout(l);
        handlers.current.onLayout?.(l);
      }
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  }, []);

  // Live switch -- never through the URL (that would reload the frame).
  useEffect(() => {
    post({ type: "mrp:theme", theme });
  }, [theme]);

  // Only the INITIAL page and mode go in the URL -- later page switches are
  // posted to the frame ("mrp:selectPage") so it never reloads. The mode is
  // re-read whenever the frame is deliberately reloaded (reloadKey).
  const initialPage = useRef(page).current;
  const urlTheme = useMemo(() => themeRef.current, [reloadKey, reportId, source]);
  const params = new URLSearchParams({ reportId, mode: source, embed: "1", theme: urlTheme });
  if (source === "share" && shareToken) params.set("token", shareToken);
  if (initialPage) params.set("page", initialPage);
  if (design) params.set("design", "1");
  if (fit) params.set("fit", fit);
  if (adaptive) params.set("adaptive", "1");
  if (awaitDefinition) params.set("await", "definition");
  const showToggle = themeToggle ?? forced === undefined;

  return (
    <div className="st-tvf" style={{ background: PREVIEW_STAGE_BG[theme], borderRadius: rounded ? 10 : 0, boxShadow: rounded ? "var(--shadow-md)" : undefined, aspectRatio: adaptive ? String(aspect) : undefined }}>
      {!ready ? (
        <div style={{ position: "absolute", inset: 0, padding: 24, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gridTemplateRows: "60px 1fr 1fr", gap: 12, opacity: 0.35 }}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} height="100%" style={{ background: PREVIEW_SKELETON_BG[theme] }} />)}
          <Skeleton height="100%" style={{ gridColumn: "span 3", background: PREVIEW_SKELETON_BG[theme] }} />
          <Skeleton height="100%" style={{ background: PREVIEW_SKELETON_BG[theme] }} />
          <Skeleton height="100%" style={{ gridColumn: "span 4", background: PREVIEW_SKELETON_BG[theme] }} />
        </div>
      ) : null}
      <iframe
        key={`${reportId}-${source}-${reloadKey ?? ""}`}
        ref={iframe}
        title="Report preview"
        src={`/tv-preview/preview.html?${params.toString()}`}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, opacity: ready ? 1 : 0, transition: "opacity 200ms ease" }}
      />
      {showToggle ? (
        <div className="st-tvf__toggle">
          <PreviewThemeToggle compact theme={theme} onChange={setSaved} />
        </div>
      ) : null}
    </div>
  );
});
